import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check, Loader } from "lucide-react";
import { Button } from "@ui/Button";
import { URLS } from "@/constants/urls";
import { useMultiStepForm } from "@/hooks";

const SummaryLayout: FC<{
  children: ReactNode;
  onPayment: () => void;
  isLoadingSession: boolean;
}> = ({ onPayment, isLoadingSession, children }) => {
  const { t } = useTranslation();
  const { form } = useMultiStepForm();
  const data = [
    t("summaryPoints.point1"),
    t("summaryPoints.point2"),
    t("summaryPoints.point3"),
    t("summaryPoints.point4"),
  ];

  return (
    <>
      <header className="space-y-[29px] lg:space-y-16">
        <h1 className="text-center text-xl font-bold sm:text-start sm:text-2xl lg:text-[34px]">
          {t("summaryTitle")}
        </h1>
        <div className="space-y-8 px-4 py-6 text-center lg:px-6 lg:py-10 lg:pb-[72px]">
          <h1 className="text-xl font-bold sm:text-2xl lg:text-[34px]">{t("summarySubtitle")}</h1>
          <div className="mx-auto grid grid-cols-1 gap-4 lg:w-fit lg:grid-cols-[repeat(2,auto)] lg:gap-x-12 lg:gap-y-6">
            {data.map((item, index) => (
              <div className="flex flex-row items-center gap-4" key={index}>
                <div className="bg-primary text-background flex size-8 shrink-0 items-center justify-center rounded-full">
                  <Check strokeWidth={1.5} />
                </div>
                <p className="text-left">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </header>
      <div className="space-y-4 md:space-y-8">
        {children}
        <div className="flex flex-col-reverse justify-between gap-3 min-[400px]:flex-row">
          <Button
            data-test="summary-back-button"
            type="button"
            variant="secondary"
            className="flex-1 uppercase min-[400px]:flex-none"
            asChild
          >
            <Link to={`${URLS.HOME}?step=5`}>{t("back")}</Link>
          </Button>
          <Button
            data-test="summary-submit-button"
            disabled={!form.formState.isValid || isLoadingSession}
            onClick={onPayment}
            className="flex-1 uppercase min-[400px]:ml-auto min-[400px]:w-[242px] min-[400px]:flex-none"
          >
            {isLoadingSession ? <Loader className="animate-spin" /> : t("payAndOrderFullAnalysis")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default SummaryLayout;
